/**
 * ========================
 * FORMS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Forms Management domain.
 * Sections:
 *  1. Overview — Dashboard (all forms users)
 *  2. Forms — All Forms (all), Create Form (creators)
 *  3. Entries — Approval Queue (reviewers)
 *  4. Integrations — Webhooks (FormsAdmin / Forms.Webhooks.Manage)
 *  5. Administration — Settings, Lookups (FormsAdmin / Forms.Settings.Manage)
 *
 * Row-level decisions (which forms a user may edit) are the API's job — it scopes
 * every list to owned / collaborated forms and returns accessLevelID per form.
 */

import {
  IconDashboard,
  IconListDetails,
  IconFilePlus,
  IconChecklist,
  IconWebhook,
  IconSettings,
  IconDatabase,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { createVisibilityCheck } from './BaseMenuProvider';

const FORMS_ADMIN_ROLES = ['Admin', 'FormsAdmin'];

class FormsMenuProvider implements DomainMenuProvider {
  domainId = 'forms';

  getMenuConfig() {
    const allFormsUsers = createVisibilityCheck({
      roles: [...FORMS_ADMIN_ROLES, 'FormsManager', 'FormsReviewer', 'FormsViewer'],
      permissions: ['Forms.Read'],
    });
    const creators = createVisibilityCheck({
      roles: [...FORMS_ADMIN_ROLES, 'FormsManager'],
      permissions: ['Forms.Create'],
    });
    const reviewers = createVisibilityCheck({
      roles: [...FORMS_ADMIN_ROLES, 'FormsManager', 'FormsReviewer'],
      permissions: ['Forms.Entries.Approve'],
    });
    const webhookManagers = createVisibilityCheck({
      roles: FORMS_ADMIN_ROLES,
      permissions: ['Forms.Webhooks.Manage'],
    });
    const settingsManagers = createVisibilityCheck({
      roles: FORMS_ADMIN_ROLES,
      permissions: ['Forms.Settings.Manage'],
    });

    const sections: MenuSection[] = [
      {
        id: 'forms-overview',
        label: 'Overview',
        items: [
          {
            id: 'forms-dashboard',
            label: 'Dashboard',
            path: '/app/forms',
            icon: <IconDashboard size={16} />,
            visible: allFormsUsers,
          },
        ],
      },
      {
        id: 'forms-forms',
        label: 'Forms',
        items: [
          {
            id: 'forms-all-forms',
            label: 'All Forms',
            path: '/app/forms/forms',
            icon: <IconListDetails size={16} />,
            visible: allFormsUsers,
          },
          {
            id: 'forms-create-form',
            label: 'Create Form',
            path: '/app/forms/forms/create',
            icon: <IconFilePlus size={16} />,
            visible: creators,
          },
        ],
      },
      {
        id: 'forms-entries',
        label: 'Entries',
        visible: reviewers,
        items: [
          {
            id: 'forms-approvals',
            label: 'Approval Queue',
            path: '/app/forms/approvals',
            icon: <IconChecklist size={16} />,
            visible: reviewers,
          },
        ],
      },
      {
        id: 'forms-integrations',
        label: 'Integrations',
        visible: webhookManagers,
        items: [
          {
            id: 'forms-webhooks',
            label: 'Webhooks',
            path: '/app/forms/webhooks',
            icon: <IconWebhook size={16} />,
            visible: webhookManagers,
          },
        ],
      },
      {
        id: 'forms-administration',
        label: 'Administration',
        visible: settingsManagers,
        items: [
          {
            id: 'forms-settings',
            label: 'Module Settings',
            path: '/app/forms/admin/settings',
            icon: <IconSettings size={16} />,
            visible: settingsManagers,
          },
          {
            id: 'forms-lookups',
            label: 'Categories & Series',
            path: '/app/forms/admin/lookups',
            icon: <IconDatabase size={16} />,
            visible: settingsManagers,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '1.0.0',
        lastUpdated: Date.now(),
        description: 'Forms management menu',
      },
    };
  }
}

export const formsMenuProvider = new FormsMenuProvider();
